import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageLocations = () => {
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    // Tab State: 'pending' | 'states' | 'cities'
    const [activeTab, setActiveTab] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Pending Cities State
    const [pendingCities, setPendingCities] = useState([]);
    const [editingPendingId, setEditingPendingId] = useState(null);
    const [editPendingName, setEditPendingName] = useState('');

    // States list & CRUD State
    const [states, setStates] = useState([]);
    const [stateSearch, setStateSearch] = useState('');
    const [newStateName, setNewStateName] = useState('');
    const [editingStateId, setEditingStateId] = useState(null);
    const [editStateName, setEditStateName] = useState('');

    // Cities list & CRUD State
    const [cities, setCities] = useState([]);
    const [citySearch, setCitySearch] = useState('');
    const [cityStateFilter, setCityStateFilter] = useState('');
    const [newCityName, setNewCityName] = useState('');
    const [newCityStateId, setNewCityStateId] = useState('');
    const [editingCityId, setEditingCityId] = useState(null);
    const [editCityName, setEditCityName] = useState('');
    const [editCityStateId, setEditCityStateId] = useState('');
    
    const [showAddCityModal, setShowAddCityModal] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchPending();
        fetchLocations();
        // eslint-disable-next-line
    }, []);

    const fetchPending = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('action', 'fetch_pending_cities');

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setPendingCities(data.pending_cities || []);
            } else {
                setError(data.message || 'Error fetching pending cities.');
            }
        } catch (err) {
            setError('Network error.');
        } finally {
            setLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const formData = new FormData();
            formData.append('action', 'fetch_all_locations');

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setStates(data.states || []);
                setCities(data.cities || []);
            }
        } catch (err) {
            console.error('Error fetching locations:', err);
        }
    };

    // --- PENDING CITIES ACTIONS ---
    const handleApprovePending = async (id, originalName) => {
        const finalName = editingPendingId === id ? editPendingName : originalName;
        if (!finalName) {
            await customAlert('City name cannot be empty', 'Validation Error', 'warning');
            return;
        }
        
        try {
            const fd = new FormData();
            fd.append('action', 'approve_city');
            fd.append('pending_id', id);
            fd.append('corrected_name', finalName);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setEditingPendingId(null);
                fetchPending();
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error approving city.', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error.', 'Error', 'danger');
        }
    };

    const handleRejectPending = async (id) => {
        if (!await customConfirm("Are you sure you want to reject this city?", "Reject City Request", "danger")) return;
        try {
            const fd = new FormData();
            fd.append('action', 'reject_city');
            fd.append('pending_id', id);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchPending();
            } else {
                await customAlert(data.message || 'Error rejecting city.', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error.', 'Error', 'danger');
        }
    };

    // --- STATE ACTIONS ---
    const handleAddState = async (e) => {
        e.preventDefault();
        if (!newStateName.trim()) return;

        try {
            const fd = new FormData();
            fd.append('action', 'add_state');
            fd.append('name', newStateName.trim());

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setNewStateName('');
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error adding state', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleEditState = async (id) => {
        if (!editStateName.trim()) return;

        try {
            const fd = new FormData();
            fd.append('action', 'edit_state');
            fd.append('id', id);
            fd.append('name', editStateName.trim());

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setEditingStateId(null);
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error updating state', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleDeleteState = async (id) => {
        if (!await customConfirm("Are you sure you want to delete this state? This action cannot be undone.", "Delete State", "danger")) return;

        try {
            const fd = new FormData();
            fd.append('action', 'delete_state');
            fd.append('id', id);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error deleting state', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    // --- CITY ACTIONS ---
    const handleAddCity = async (e) => {
        e.preventDefault();
        if (!newCityName.trim() || !newCityStateId) {
            await customAlert('City name and State are required.', 'Validation Error', 'warning');
            return;
        }

        try {
            const fd = new FormData();
            fd.append('action', 'add_city');
            fd.append('name', newCityName.trim());
            fd.append('state_id', newCityStateId);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setNewCityName('');
                setNewCityStateId('');
                setShowAddCityModal(false);
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error adding city', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleEditCity = async (id) => {
        if (!editCityName.trim() || !editCityStateId) return;

        try {
            const fd = new FormData();
            fd.append('action', 'edit_city');
            fd.append('id', id);
            fd.append('name', editCityName.trim());
            fd.append('state_id', editCityStateId);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                setEditingCityId(null);
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error updating city', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleDeleteCity = async (id) => {
        if (!await customConfirm("Are you sure you want to delete this city?", "Delete City", "danger")) return;

        try {
            const fd = new FormData();
            fd.append('action', 'delete_city');
            fd.append('id', id);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error deleting city', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleToggleStateVisibility = async (id, currentVisibility) => {
        const nextVisibility = currentVisibility === 1 ? 0 : 1;
        try {
            const fd = new FormData();
            fd.append('action', 'toggle_state_visibility');
            fd.append('id', id);
            fd.append('is_visible', nextVisibility);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error updating state visibility', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleToggleCityVisibility = async (id, currentVisibility) => {
        const nextVisibility = currentVisibility === 1 ? 0 : 1;
        try {
            const fd = new FormData();
            fd.append('action', 'toggle_city_visibility');
            fd.append('id', id);
            fd.append('is_visible', nextVisibility);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_locations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (data.success) {
                fetchLocations();
            } else {
                await customAlert(data.message || 'Error updating city visibility', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error', 'Error', 'danger');
        }
    };

    // Filter states
    const filteredStates = states.filter(s => 
        s.name.toLowerCase().includes(stateSearch.toLowerCase())
    );

    // Filter cities
    const filteredCities = cities.filter(c => {
        const matchesSearch = c.name.toLowerCase().includes(citySearch.toLowerCase());
        const matchesState = cityStateFilter ? c.state_id.toString() === cityStateFilter.toString() : true;
        return matchesSearch && matchesState;
    });

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
                
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h2 className="sa-page-title">Location Management</h2>
                        <p className="sa-page-subtitle">Configure system states, cities, and process custom locations requested by users.</p>
                    </div>
                </div>

                {/* Tab Switcher */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: '1.5rem' }}>
                    <button 
                        onClick={() => setActiveTab('pending')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'pending' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'pending' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        Pending Approvals ({pendingCities.length})
                    </button>
                    <button 
                        onClick={() => setActiveTab('states')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'states' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'states' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        States ({states.length})
                    </button>
                    <button 
                        onClick={() => setActiveTab('cities')} 
                        style={{
                            padding: '10px 20px', background: 'none', border: 'none', 
                            color: activeTab === 'cities' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            borderBottom: activeTab === 'cities' ? '2px solid var(--accent-primary)' : 'none',
                            fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                        }}
                    >
                        Cities ({cities.length})
                    </button>
                </div>

                {error && (
                    <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', padding: '10px 15px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>{error}
                    </div>
                )}

                {/* --- PENDING TAB --- */}
                {activeTab === 'pending' && (
                    <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                        <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                            <table className="sa-table" style={{ margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>State</th>
                                        <th>Requested City Name</th>
                                        <th>Requested By (Restaurant)</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: 'center', padding: '3rem' }}>
                                                <i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: 'var(--accent-primary)', marginBottom: '0.5rem' }}></i>
                                                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Loading pending queue...</p>
                                            </td>
                                        </tr>
                                    ) : pendingCities.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                                                No pending city requests.
                                            </td>
                                        </tr>
                                    ) : (
                                        pendingCities.map(pc => (
                                            <tr key={pc.id}>
                                                <td>{new Date(pc.created_at || Date.now()).toLocaleDateString()}</td>
                                                <td><span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{pc.state_name}</span></td>
                                                <td>
                                                    {editingPendingId === pc.id ? (
                                                        <input 
                                                            type="text" 
                                                            value={editPendingName}
                                                            onChange={e => setEditPendingName(e.target.value)}
                                                            className="sa-input"
                                                            style={{ padding: '6px 10px', width: '180px', fontSize: '0.85rem' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{pc.city_name}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{pc.restaurant_name}</div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>ID: #{pc.user_id}</div>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                        {editingPendingId === pc.id ? (
                                                            <>
                                                                <button 
                                                                    onClick={() => handleApprovePending(pc.id, pc.city_name)} 
                                                                    className="sa-action-btn success"
                                                                >
                                                                    Save & Approve
                                                                </button>
                                                                <button 
                                                                    onClick={() => setEditingPendingId(null)} 
                                                                    className="sa-action-btn secondary"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button 
                                                                    onClick={() => handleApprovePending(pc.id, pc.city_name)} 
                                                                    className="sa-action-btn success"
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button 
                                                                    onClick={() => { setEditingPendingId(pc.id); setEditPendingName(pc.city_name); }} 
                                                                    className="sa-action-btn primary"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleRejectPending(pc.id)} 
                                                                    className="sa-action-btn danger"
                                                                >
                                                                    Reject
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* --- STATES TAB --- */}
                {activeTab === 'states' && (
                    <div>
                        {/* Add State form */}
                        <div className="sa-panel" style={{ padding: '20px', marginBottom: '1.5rem' }}>
                            <form onSubmit={handleAddState} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>Add New State</label>
                                    <input 
                                        type="text" 
                                        className="sa-input" 
                                        placeholder="e.g. Rajasthan" 
                                        value={newStateName} 
                                        onChange={e => setNewStateName(e.target.value)} 
                                        style={{ maxWidth: '400px' }}
                                    />
                                </div>
                                <button type="submit" className="sa-primary-btn" style={{ padding: '0.6rem 1.2rem', marginTop: '22px' }}>
                                    <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>Add State
                                </button>
                            </form>
                        </div>

                        {/* Search & List */}
                        <div className="sa-panel" style={{ padding: '15px', marginBottom: '1rem', display: 'flex', gap: '15px' }}>
                            <input 
                                type="text" 
                                className="sa-input" 
                                placeholder="Search state name..." 
                                value={stateSearch} 
                                onChange={e => setStateSearch(e.target.value)} 
                                style={{ maxWidth: '300px', padding: '8px 12px' }}
                            />
                        </div>

                        <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                <table className="sa-table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '80px' }}>ID</th>
                                            <th>State Name</th>
                                            <th style={{ width: '120px', textAlign: 'center' }}>Visibility</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStates.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                                    No states found.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredStates.map(s => (
                                                <tr key={s.id}>
                                                    <td style={{ color: 'var(--text-secondary)' }}>#{s.id}</td>
                                                    <td>
                                                        {editingStateId === s.id ? (
                                                            <input 
                                                                type="text" 
                                                                className="sa-input" 
                                                                value={editStateName} 
                                                                onChange={e => setEditStateName(e.target.value)}
                                                                style={{ padding: '4px 8px', maxWidth: '300px' }}
                                                            />
                                                        ) : (
                                                            <strong style={{ color: 'var(--text-primary)' }}>{s.name}</strong>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button 
                                                            onClick={() => handleToggleStateVisibility(s.id, s.is_visible)} 
                                                            className={`sa-action-btn ${s.is_visible ? 'success' : 'secondary'}`}
                                                            title={s.is_visible ? 'Visible (Click to Hide)' : 'Hidden (Click to Show)'}
                                                            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                                                        >
                                                            <i className={`fas ${s.is_visible ? 'fa-eye' : 'fa-eye-slash'}`}></i> {s.is_visible ? 'Active' : 'Hidden'}
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                            {editingStateId === s.id ? (
                                                                <>
                                                                    <button onClick={() => handleEditState(s.id)} className="sa-action-btn success">Save</button>
                                                                    <button onClick={() => setEditingStateId(null)} className="sa-action-btn secondary">Cancel</button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button onClick={() => { setEditingStateId(s.id); setEditStateName(s.name); }} className="sa-action-btn primary">Edit</button>
                                                                    <button onClick={() => handleDeleteState(s.id)} className="sa-action-btn danger">Delete</button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- CITIES TAB --- */}
                {activeTab === 'cities' && (
                    <div>
                        {/* Quick controls */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '15px', flex: 1, flexWrap: 'wrap' }}>
                                <input 
                                    type="text" 
                                    className="sa-input" 
                                    placeholder="Search city name..." 
                                    value={citySearch} 
                                    onChange={e => setCitySearch(e.target.value)} 
                                    style={{ maxWidth: '280px', padding: '8px 12px' }}
                                />
                                <select 
                                    className="sa-select" 
                                    value={cityStateFilter} 
                                    onChange={e => setCityStateFilter(e.target.value)}
                                    style={{ maxWidth: '200px', padding: '8px 12px' }}
                                >
                                    <option value="">All States</option>
                                    {states.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button 
                                onClick={() => setShowAddCityModal(true)} 
                                className="sa-primary-btn"
                                style={{ padding: '0.6rem 1.2rem' }}
                            >
                                <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>Add City
                            </button>
                        </div>

                        {/* List Cities */}
                        <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                <table className="sa-table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '80px' }}>ID</th>
                                            <th>City Name</th>
                                            <th>State</th>
                                            <th style={{ width: '120px', textAlign: 'center' }}>Visibility</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCities.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                                    No cities found.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredCities.map(c => (
                                                <tr key={c.id}>
                                                    <td style={{ color: 'var(--text-secondary)' }}>#{c.id}</td>
                                                    <td>
                                                        {editingCityId === c.id ? (
                                                            <input 
                                                                type="text" 
                                                                className="sa-input" 
                                                                value={editCityName} 
                                                                onChange={e => setEditCityName(e.target.value)}
                                                                style={{ padding: '4px 8px', maxWidth: '250px' }}
                                                            />
                                                        ) : (
                                                            <strong style={{ color: 'var(--text-primary)' }}>{c.name}</strong>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingCityId === c.id ? (
                                                            <select 
                                                                className="sa-select" 
                                                                value={editCityStateId} 
                                                                onChange={e => setEditCityStateId(e.target.value)}
                                                                style={{ padding: '4px 8px', maxWidth: '200px' }}
                                                            >
                                                                {states.map(s => (
                                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{c.state_name}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button 
                                                            onClick={() => handleToggleCityVisibility(c.id, c.is_visible)} 
                                                            className={`sa-action-btn ${c.is_visible ? 'success' : 'secondary'}`}
                                                            title={c.is_visible ? 'Visible (Click to Hide)' : 'Hidden (Click to Show)'}
                                                            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                                                        >
                                                            <i className={`fas ${c.is_visible ? 'fa-eye' : 'fa-eye-slash'}`}></i> {c.is_visible ? 'Active' : 'Hidden'}
                                                        </button>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                            {editingCityId === c.id ? (
                                                                <>
                                                                    <button onClick={() => handleEditCity(c.id)} className="sa-action-btn success">Save</button>
                                                                    <button onClick={() => setEditingCityId(null)} className="sa-action-btn secondary">Cancel</button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button onClick={() => { setEditingCityId(c.id); setEditCityName(c.name); setEditCityStateId(c.state_id); }} className="sa-action-btn primary">Edit</button>
                                                                    <button onClick={() => handleDeleteCity(c.id)} className="sa-action-btn danger">Delete</button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for Adding City */}
                {showAddCityModal && (
                    <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, padding: '1rem' }}>
                        <div className="sa-panel" style={{ width: '100%', maxWidth: '500px', padding: '25px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}><i className="fas fa-city" style={{ color: 'var(--accent-primary)', marginRight: '10px' }}></i>Add New City</h3>
                                <button onClick={() => setShowAddCityModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.2rem', cursor: 'pointer' }}><i className="fas fa-times"></i></button>
                            </div>

                            <form onSubmit={handleAddCity}>
                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                    <label>State *</label>
                                    <select 
                                        className="sa-select" 
                                        required 
                                        value={newCityStateId} 
                                        onChange={e => setNewCityStateId(e.target.value)}
                                    >
                                        <option value="">-- Choose State --</option>
                                        {states.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                    <label>City Name *</label>
                                    <input 
                                        type="text" 
                                        className="sa-input" 
                                        required 
                                        placeholder="e.g. Ajmer" 
                                        value={newCityName} 
                                        onChange={e => setNewCityName(e.target.value)} 
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowAddCityModal(false)} 
                                        className="sa-secondary-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="sa-primary-btn"
                                    >
                                        Add City
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default ManageLocations;
